import type { AgentWorkerOptions } from "../agents/types.js";

const argumentMap = (argv: readonly string[]): Map<string, string> => {
  const result = new Map<string, string>();
  for (let index = 2; index < argv.length; index += 2) {
    const key = argv[index];
    const value = argv[index + 1];
    if (!key?.startsWith("--") || value === undefined) {
      throw new Error(`Invalid worker argument near ${key ?? "<end>"}`);
    }
    result.set(key.slice(2), value);
  }
  return result;
};

const required = (args: Map<string, string>, name: string): string => {
  const value = args.get(name);
  if (!value) throw new Error(`Missing worker argument: --${name}`);
  return value;
};

const optional = (args: Map<string, string>, name: string): string | undefined =>
  args.get(name) || undefined;

export const parseWorkerOptions = (
  argv: readonly string[] = process.argv,
): AgentWorkerOptions => {
  const args = argumentMap(argv);
  const model = optional(args, "model");
  const thinking = optional(args, "thinking");
  const fabricExtensionPath = optional(args, "fabric-extension");
  const schemaFile = optional(args, "schema-file");
  const imagesFile = optional(args, "images-file");
  const systemPrompt = optional(args, "system-prompt");
  const sessionFile = optional(args, "session-file");
  const sessionExportFile = optional(args, "session-export-file");
  const actorId = optional(args, "actor-id");
  const actorName = optional(args, "actor-name");
  const capabilityRequirementsSource = optional(args, "capability-requirements");
  const capabilityDigest = optional(args, "capability-digest");
  const capabilityRequirements = capabilityRequirementsSource
    ? JSON.parse(capabilityRequirementsSource) as unknown
    : undefined;
  if (
    capabilityRequirements !== undefined &&
    (!Array.isArray(capabilityRequirements) ||
      capabilityRequirements.length > 128 ||
      capabilityRequirements.some((ref) => typeof ref !== "string" || ref.length > 256 || !ref.includes(".")))
  ) {
    throw new Error("Invalid worker capability requirements");
  }
  const meshRoot = optional(args, "mesh-root");
  const projectRoot = optional(args, "project-root");
  const ownerHostId = optional(args, "owner-host-id");
  const ownerIdentityId = optional(args, "owner-identity-id");
  const runRoot = optional(args, "run-root");
  const steerFile = optional(args, "steer-file");
  const branch = optional(args, "branch");
  const worktree = optional(args, "worktree");
  const maxTokens = optional(args, "max-tokens");
  const runnerSessionId = optional(args, "runner-session-id");
  const mainAgentId = optional(args, "main-agent-id");
  const fabricSessionId = optional(args, "fabric-session-id");
  const runner = required(args, "runner");
  if (runner !== "pi" && runner !== "claude" && runner !== "veda") {
    throw new Error(`Unsupported Fabric agent runner: ${runner}`);
  }
  const extensions = required(args, "extensions") === "true";
  const selectedKernel = args.get("kernel");
  const pythonRuntime = args.get("python-runtime") ?? "monty";
  if (pythonRuntime !== "cpython" && pythonRuntime !== "monty") {
    throw new Error(`Invalid worker Python runtime: ${pythonRuntime}`);
  }
  if (selectedKernel !== undefined && selectedKernel !== "typescript" && selectedKernel !== "python") {
    throw new Error(`Invalid worker kernel: ${selectedKernel}`);
  }
  if (selectedKernel !== undefined && (runner !== "pi" || !extensions)) {
    throw new Error("Explicit worker kernel requires the Pi runner with Fabric extensions enabled");
  }
  // Old launchers had no flag and always used TypeScript, regardless of ambient env.
  const kernel = runner === "pi" && extensions ? selectedKernel ?? "typescript" : undefined;
  return {
    id: required(args, "id"),
    runner,
    ...(kernel ? { kernel, pythonRuntime } : {}),
    name: required(args, "name"),
    taskFile: required(args, "task-file"),
    ...(imagesFile ? { imagesFile } : {}),
    statusFile: required(args, "status-file"),
    lifecycleFile: required(args, "lifecycle-file"),
    logFile: required(args, "log-file"),
    ...(schemaFile ? { schemaFile } : {}),
    cwd: required(args, "cwd"),
    piBinary: required(args, "pi-binary"),
    claudeBinary: required(args, "claude-binary"),
    vedaBinary: required(args, "veda-binary"),
    vedaBackend: required(args, "veda-backend"),
    vedaPersona: required(args, "veda-persona"),
    timeoutMs: Number(required(args, "timeout-ms")),
    depth: Number(required(args, "depth")),
    fullCodeMode: required(args, "full-code-mode") === "true",
    ...(mainAgentId ? { mainAgentId } : {}),
    ...(fabricSessionId ? { fabricSessionId } : {}),
    extensions,
    tools: JSON.parse(required(args, "tools")) as string[],
    grantedRisks: JSON.parse(required(args, "granted-risks")) as string[],
    transport: required(args, "transport") as AgentWorkerOptions["transport"],
    ...(fabricExtensionPath ? { fabricExtensionPath } : {}),
    ...(model ? { model } : {}),
    ...(thinking ? { thinking } : {}),
    ...(systemPrompt ? { systemPrompt } : {}),
    ...(sessionFile ? { sessionFile } : {}),
    ...(sessionExportFile ? { sessionExportFile } : {}),
    ...(actorId ? { actorId } : {}),
    ...(actorName ? { actorName } : {}),
    ...(capabilityRequirements
      ? { capabilityRequirements: [...new Set(capabilityRequirements as string[])] }
      : {}),
    ...(capabilityDigest ? { capabilityDigest } : {}),
    ...(meshRoot ? { meshRoot } : {}),
    ...(projectRoot ? { projectRoot } : {}),
    ...(ownerHostId ? { ownerHostId } : {}),
    ...(ownerIdentityId ? { ownerIdentityId } : {}),
    ...(runnerSessionId ? { runnerSessionId } : {}),
    ...(runRoot ? { runRoot } : {}),
    ...(steerFile ? { steerFile } : {}),
    ...(branch ? { branch } : {}),
    ...(worktree ? { worktree } : {}),
    ...(maxTokens ? { maxTokens: Number(maxTokens) } : {}),
  };
};
